import api from './api';
import { API_ENDPOINTS, USE_MOCK_DATA } from '../constants/config';
import { getMockCompanies } from '../data/mockData';

// Temporary mock data for Sites
let mockSites = [
  {
    id: 1,
    name: 'HQ Building',
    location: 'Bengaluru',
    address: '123 Tech Park, Whitefield',
    company_id: 1,
    company: { company_name: 'TechCorp Industries' },
    spoc: { name: 'David Wilson' },
    team_lead: { name: 'Sarah Jenkins' },
    contract_id: 'AMC-2026-001',
    active_tickets: 5,
    spocs_count: 1,
    team_leads_count: 1,
    active_contracts: 1
  }
];

class SiteService {
  async getAllSites() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, data: mockSites });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.SITES);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching sites:', error);
      return { success: false, error: error.message };
    }
  }

  async getSitesByCompany(companyId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const filtered = mockSites.filter(s => s.company_id === parseInt(companyId));
          resolve({ success: true, data: filtered });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.SITES}?company_id=${companyId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching company sites:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getSiteContracts(siteId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ 
            success: true, 
            data: [{
              id: ' AMC-2026-0001',
              site_id: siteId,
              is_enabled: true,
              services: {
                comprehensive: ['Fire Alarm System (FAS)', 'Access Control System (ACS)'],
                non_comprehensive: []
              }
            }]
          });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.CONTRACTS_BY_SITE(siteId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching site contracts:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async createSite(siteData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const companies = getMockCompanies();
          const selectedCompany = companies.find((company) => company.id === siteData.company_id);
          const newSite = {
            id: mockSites.length + 100,
            ...siteData,
            company_name: selectedCompany?.company_name || '',
            company: selectedCompany ? { company_name: selectedCompany.company_name } : undefined,
            active_tickets: 0,
            spocs_count: 0,
            team_leads_count: 0,
            active_contracts: 0
          };
          mockSites.unshift(newSite);
          resolve({ success: true, data: newSite });
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.SITES, siteData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating site:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async updateSite(siteId, siteData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const siteIndex = mockSites.findIndex(s => s.id === siteId);
          if (siteIndex !== -1) {
            mockSites[siteIndex] = {
              ...mockSites[siteIndex],
              ...siteData
            };
            resolve({ success: true, data: mockSites[siteIndex] });
          } else {
            resolve({ success: false, error: 'Site not found' });
          }
        }, 500);
      });
    }

    try {
      const response = await api.put(API_ENDPOINTS.SITE_DETAILS(siteId), siteData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating site:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async deleteSite(siteId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const siteIndex = mockSites.findIndex(s => s.id === siteId);
          if (siteIndex !== -1) {
            const site = mockSites[siteIndex];
            
            // Check dependencies
            if (site.spocs_count > 0 || site.team_leads_count > 0 || site.active_contracts > 0) {
              resolve({ 
                success: false, 
                error: `Cannot delete site with active SPOCs, Team Leads, or Contracts attached.` 
              });
            } else {
              mockSites.splice(siteIndex, 1);
              resolve({ success: true });
            }
          } else {
            resolve({ success: false, error: 'Site not found' });
          }
        }, 500);
      });
    }

    try {
      await api.delete(API_ENDPOINTS.SITE_DETAILS(siteId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting site:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getSiteById(siteId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const site = mockSites.find(s => s.id === siteId);
          if (site) {
            resolve({ success: true, data: site });
          } else {
            resolve({ success: false, error: 'Site not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.SITE_DETAILS(siteId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching site:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getSiteStats(siteId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const site = mockSites.find(s => s.id === siteId);
          if (site) {
            resolve({ 
              success: true, 
              data: { 
                site_id: siteId, 
                total_tickets: site.active_tickets + 10, 
                open_tickets: site.active_tickets 
              } 
            });
          } else {
            resolve({ success: false, error: 'Site not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.SITE_STATS(siteId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching site stats:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async exportSitesCSV() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const headers = [
            "Site ID",
            "Site Name",
            "Company Name",
            "Address",
            "Contact Person",
            "Contact Number",
            "Active Tickets",
            "Active Contracts Count",
            "PO Number",
            "SPOCs Count",
            "Team Leads Count"
          ];

          const rows = mockSites.map((s) => [
            s.id,
            `"${(s.name || "").replace(/"/g, '""')}"`,
            `"${(s.company?.company_name || s.company_name || "").replace(/"/g, '""')}"`,
            `"${(s.address || "").replace(/"/g, '""')}"`,
            `"${(s.contact_person || "").replace(/"/g, '""')}"`,
            `"${(s.contact_number || "").replace(/"/g, '""')}"`,
            s.active_tickets || 0,
            s.active_contracts || 0,
            `"${(s.po_number || "").replace(/"/g, '""')}"`,
            s.spocs_count || 0,
            s.team_leads_count || 0
          ]);

          const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
          const blob = new Blob([csv], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `sites-export-${Date.now()}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);

          resolve({ success: true });
        }, 500);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.SITES_EXPORT_CSV, { responseType: "blob" });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `sites-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("Error exporting sites:", error);
      return { success: false, error: error.message };
    }
  }
}

export default new SiteService();